/**
 * PLAT-11099 — the frontend half of a reel chapterization.
 *
 * A sibling of `AITextGenerationRepository`, not an extension of it. The two
 * post to the same endpoint but disagree about the one thing that matters here:
 * a naming run returns a line of text and `parseAITextOptions` normalises
 * anything into `string[]`, which would reduce this prompt's JSON array of
 * objects to nothing at all. So this returns the endpoint's `result` **raw and
 * unvalidated** — the output contract belongs to the prompt record (PLAT-10915
 * DS5), and reading it is `chapterizationResponse`'s job.
 *
 * It composes no prompt text: the record, the Brand Kit layering and the model
 * choice are all server-side, exactly as for every other caller.
 */
import { authenticatedFetch } from '@src/utility/authenticatedFetch';

/** Exported so a caller's AI Prompt Log record names the route that ran it. */
export const PROMPT_GENERATE_ENDPOINT = '/api/aiprompts/prompt-generate';

function getApiBase(): string {
  return process.env.REACT_APP_API_URL ?? '';
}

/**
 * Typed failure the overlay renders verbatim, so a misconfigured prompt id shows
 * the server's reason rather than a bare status code.
 */
export class ReelChapterizationError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ReelChapterizationError';
    this.status = status;
  }
}

export interface ReelChapterizationRequest {
  /** DatoCMS `ai_prompt` record id, resolved by title (PLAT-11152). */
  promptId: string;
  /** Context BY REFERENCE — the backend resolves the Brand Kit from it. */
  storyId?: string;
  showcaseId?: string;
  clipId?: string;
  /** The composed reel (`reelChapterizationBaseText`). */
  baseText: string;
  /** The overlay's fields as the prompt's instruction block. */
  instructions: string;
  signal?: AbortSignal;
}

/**
 * Run the chapterization prompt and hand back whatever it produced.
 *
 * Throws `ReelChapterizationError` on a non-2xx response or an unreachable API.
 * It does **not** throw on a response it cannot make sense of — that is a
 * validation outcome, not a transport one, and the caller decides what an
 * unusable response means.
 */
export async function generateReelChapterization(
  request: ReelChapterizationRequest,
): Promise<unknown> {
  const {
    promptId,
    storyId,
    showcaseId,
    clipId,
    baseText,
    instructions,
    signal,
  } = request;

  let response: Response;
  try {
    response = await authenticatedFetch(
      `${getApiBase()}${PROMPT_GENERATE_ENDPOINT}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          promptId,
          context: { storyId, showcaseId, clipId },
          inputs: {
            // `scope` is part of the endpoint's `baseText` shape and its union
            // is 'clip' | 'selection'; `composeMessages` never reads it, only
            // `.text` reaches the model. A reel is the whole clip, so 'clip'.
            baseText: { scope: 'clip', text: baseText },
            instructions,
          },
        }),
        signal,
      },
    );
  } catch (error) {
    throw new ReelChapterizationError(
      error instanceof Error ? error.message : 'Could not reach the AI service',
    );
  }

  if (!response.ok) {
    throw new ReelChapterizationError(
      await readServerError(response),
      response.status,
    );
  }

  const body = (await response.json()) as { result?: unknown };
  return body?.result;
}

/**
 * The message is rendered verbatim in the overlay, so an unstructured body (an
 * HTML error page, a proxy's stack trace) is truncated rather than spilled into
 * it. A structured `{ error }` is the server's own message and passes whole.
 */
const MAX_RAW_ERROR_CHARS = 200;

async function readServerError(response: Response): Promise<string> {
  const fallback = `Chapterization failed: ${response.status} ${response.statusText}`;
  try {
    const raw = await response.text();
    if (!raw) return fallback;
    try {
      const parsed = JSON.parse(raw) as { error?: unknown };
      if (typeof parsed.error === 'string') return parsed.error;
    } catch {
      // Not JSON — fall through to the truncated raw body.
    }
    return raw.length > MAX_RAW_ERROR_CHARS
      ? `${raw.slice(0, MAX_RAW_ERROR_CHARS)}…`
      : raw;
  } catch {
    return fallback;
  }
}
